import { Outlet, Link, createRootRoute, useNavigate } from "@tanstack/react-router";
import {
  exitImpersonation,
  getImpersonationSession,
  type ImpersonationSession,
} from "@/lib/impersonation";
import { AnimatePresence } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { useAuthStore } from "../store/authStore";
import { applyThemeToDocument, useThemeStore } from "../store/themeStore";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import ErrorBoundary from "@/components/shared/ErrorBoundary";
import { FullPageLoader, FullPageState } from "@/components/shared/FullPageState";

function NotFoundComponent() {
  return (
    <FullPageState
      code="404"
      title="Page not found"
      description="The page you are looking for does not exist or has been moved."
      actions={
        <Button asChild>
          <Link to="/">Go home</Link>
        </Button>
      }
    />
  );
}

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
});

function LoadingShell() {
  return <FullPageLoader />;
}

/**
 * An impersonated tab is a real session on somebody else's account, so it says
 * so permanently at the top of the viewport and offers a one-click way out.
 */
function ImpersonationBanner() {
  const [session, setSession] = useState<ImpersonationSession | null>(null);

  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setSession(getImpersonationSession());
  }, []);

  // The portal shell is exactly one viewport tall; publish the banner's height so
  // the shell can subtract it instead of pushing the sidebar footer off-screen.
  useEffect(() => {
    const el = ref.current;
    const root = document.documentElement;
    if (!el) return;
    const sync = () => root.style.setProperty("--banner-h", `${el.offsetHeight}px`);
    sync();
    const observer = new ResizeObserver(sync);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.removeProperty("--banner-h");
    };
  }, [session]);

  if (!session) return null;

  return (
    <div
      ref={ref}
      role="status"
      className="sticky top-0 z-[2000] flex flex-wrap items-center justify-center gap-3 border-b border-warning/40 bg-warning px-4 py-2 text-[13px] font-medium text-warning-foreground"
    >
      <span>
        Viewing as {session.name} ({session.email}) — this is their {session.role} portal, not
        your admin account.
      </span>
      <button
        type="button"
        onClick={exitImpersonation}
        className="rounded-sm border border-current/40 px-3 py-1 text-xs font-medium transition-colors hover:bg-black/10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-current"
      >
        Exit and return to admin
      </button>
    </div>
  );
}

function RootComponent() {
  const { checkSession, isHydrated, user, logout } = useAuthStore();
  const mode = useThemeStore((s) => s.mode);
  const navigate = useNavigate();

  useEffect(() => {
    applyThemeToDocument(mode);
  }, [mode]);

  useEffect(() => {
    checkSession();
  }, [checkSession]);

  useEffect(() => {
    if (user && !user.isDemo) {
      import("@/lib/push-notifications").then(({ subscribeToPush }) => {
        subscribeToPush();
      });
    }
  }, [user]);

  useEffect(() => {
    const handler = () => {
      logout();
      navigate({ to: "/auth/login" });
    };
    window.addEventListener("auth:unauthorized", handler);
    return () => window.removeEventListener("auth:unauthorized", handler);
  }, [logout, navigate]);

  if (!isHydrated) {
    return <LoadingShell />;
  }

  return (
    <TooltipProvider delayDuration={300}>
      <ImpersonationBanner />
      <ErrorBoundary>
        <AnimatePresence mode="wait">
          <Outlet />
        </AnimatePresence>
      </ErrorBoundary>
      <Toaster />
    </TooltipProvider>
  );
}
